<script lang="ts">
  import {
    fetchLegendFor,
    fitMarks,
    labelledStops,
    type LegendSpec,
    type LegendStop,
  } from '../map/layers/legendSource'
  import { getLayerDef } from '../map/layers/registry'
  import { layers } from '../state/layers.svelte'

  /**
   * The colour bar for the topmost active overlay that publishes one, named.
   * Stacking radar over NDFD temperature under one unlabelled bar would be a
   * lie, so the bar always says which layer it belongs to — and the hazards
   * layer, whose legend is two hundred polygon rules rather than a ramp, hands
   * the slot down to whatever is under it instead of blanking it.
   *
   * Stops are spaced evenly by index rather than by value: a GeoServer ramp
   * routinely carries an out-of-range sentinel (NDFD temperature opens at
   * -500 °F) that would otherwise squash the whole gradient into one corner.
   */

  let {
    compact = false,
    creditFor = () => null,
  }: {
    compact?: boolean
    /**
     * Who to credit for a layer MapLibre cannot credit itself.
     *
     * `AttributionControl` reads `attribution` off **sources in use**, and a
     * custom WebGL layer has no source — so from Phase 7 a model plate's
     * credit would silently vanish, which is exactly the kind of thing nobody
     * notices for a year. The colour bar is on screen whenever a model layer
     * is active and already names the layer, so it is the honest place for it.
     * Null means MapLibre is already showing it and this prints nothing.
     */
    creditFor?: (id: string) => string | null
  } = $props()

  let collapsed = $state(false)
  let spec = $state<LegendSpec | null>(null)
  let credit = $state<string | null>(null)

  let def = $derived(
    [...layers.active]
      .reverse()
      .map(getLayerDef)
      .find((d) => d?.legend),
  )

  $effect(() => {
    // **Re-read when the stack finishes loading, not when it changes.** A
    // layer learns who to credit from the catalog during `load()`, which
    // resolves well after the row is in `layers.active` — so reading it on the
    // stack alone gets `null` every time and the credit never appears. Looked
    // at 2026-09-20; it was in the DOM as nothing at all.
    //
    // `layers.loading` and `layers.times` are what the controller pushes back
    // after a batch of `load()` calls resolves and `addAll()` has run, which is
    // exactly the moment the answer becomes available.
    void layers.active
    void layers.loading
    void layers.times
    credit = def ? creditFor(def.id) : null
  })

  $effect(() => {
    const ref = def?.legend
    if (!ref) {
      spec = null
      return
    }
    let cancelled = false
    // Failure is silent: a missing colour bar is not worth a toast.
    void fetchLegendFor(ref).then((s) => {
      if (!cancelled) spec = s
    })
    return () => {
      cancelled = true
    }
  })

  function gradientOf(stops: readonly LegendStop[]): string {
    return `linear-gradient(to right, ${stops
      .map((s, i) => `${s.color} ${(i / Math.max(1, stops.length - 1)) * 100}%`)
      .join(', ')})`
  }

  let gradient = $derived(spec ? gradientOf(spec.stops) : '')

  /**
   * Two named bars instead of one, for the radar map's rain and snow. Each is
   * half the single bar's width, and ticked only at its ends, which is all
   * the words fit: "Light" at one end and "Heavy" at the other.
   */
  let bands = $derived(
    (spec?.bands ?? []).map((band) => ({
      label: band.label,
      gradient: gradientOf(band.stops),
      marks: labelledStops(band.stops, 2).map((stop) => ({
        label: stop.label,
        left: (band.stops.indexOf(stop) / Math.max(1, band.stops.length - 1)) * 100,
      })),
    })),
  )
  // What the marks span: 1B's 180 px bar on desktop. A phone's bar is
  // `flex: 1` and about as wide once the name and the units have their share.
  let barWidth = 180
  let marks = $derived(
    spec ? labelledStops(spec.stops, fitMarks(spec.stops, barWidth, compact ? 4 : 6)) : [],
  )

  function markPercent(index: number): number {
    if (!spec) return 0
    const i = spec.stops.indexOf(marks[index])
    return (i / Math.max(1, spec.stops.length - 1)) * 100
  }
</script>

{#if spec && def}
  <!-- The top strip of the map's card (Phase 10 plan §6): the name, the
       layer's own ramp and its units on one line, over the slider. The ramp
       is always the layer's real one; 1B's red gradient was a placeholder. -->
  <div class="legend" class:compact class:collapsed>
    <div class="strip">
      <button
        type="button"
        class="legend-head touch-target"
        aria-expanded={!collapsed}
        onclick={() => (collapsed = !collapsed)}
      >
        {#if spec.note}
          <!-- A line rather than a ramp: an isobar layer draws one colour, so
               a gradient swatch would be a swatch of nothing. -->
          <span class="rule" aria-hidden="true"></span>
        {:else if collapsed}
          <!-- Collapsed, the strip keeps a picture of the ramp. -->
          <span class="swatch" style:background={gradient} aria-hidden="true"></span>
        {/if}
        <span class="title">{def.label}</span>
      </button>
      {#if !collapsed && bands.length > 0}
        {#each bands as band (band.label)}
          <div class="scale band">
            <span class="band-name">{band.label}</span>
            <div class="band-ramp">
              <div class="bar" style:background={band.gradient} aria-hidden="true"></div>
              <div class="marks" aria-hidden="true">
                {#each band.marks as mark (mark.label)}
                  <span style:left="{mark.left}%">{mark.label}</span>
                {/each}
              </div>
            </div>
          </div>
        {/each}
        <span class="visually-hidden">
          {def.label} colour scales: {bands.map((b) => b.label).join(' and ')}, light to heavy
        </span>
      {:else if !collapsed && !spec.note}
        <div class="scale">
          <div class="bar" style:background={gradient} aria-hidden="true"></div>
          <div class="marks" aria-hidden="true">
            {#each marks as mark, i (mark.value + mark.label)}
              <span style:left="{markPercent(i)}%">{mark.label}</span>
            {/each}
          </div>
        </div>
        <span class="visually-hidden">
          {def.label} colour scale, {spec.stops[0].label} to {spec.stops[spec.stops.length - 1]
            .label}
          {spec.units}
        </span>
      {/if}
      {#if spec.units}<span class="units">{spec.units}</span>{/if}
    </div>
    {#if !collapsed}
      {#if spec.note}
        <!-- A contour layer's legend is its *interval* and a barb's is the
             convention that decodes it. Both are one sentence, and both come
             out of the catalog's own block — nothing here restates what the
             field is. -->
        <p class="note">{spec.note}</p>
      {/if}
      {#if credit}
        <!-- NOAA's credit, because a custom WebGL layer has no source for
             MapLibre's own attribution control to read one off. -->
        <p class="credit">{credit}</p>
      {/if}
    {/if}
  </div>
{/if}

<style>
  /* No box of its own: `MapView`'s card is the box, and the slider under this
     strip draws the 2 px rule between them. */
  .legend {
    padding: 6px 14px 8px;
    font-size: 12px;
  }

  .legend.compact {
    padding: 4px 12px 6px;
    font-size: 11px;
  }

  .strip {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .compact .strip {
    gap: 10px;
  }

  .legend-head {
    flex: 0 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    padding: 2px 0;
    cursor: pointer;
    text-align: left;
    font-size: inherit;
    font-weight: 800;
  }

  /* A vector row has no bar, so the head takes the room and the units stay at
     the far end. */
  .legend.collapsed .legend-head,
  .strip:not(:has(.scale)) .legend-head {
    flex-grow: 1;
  }

  .title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .swatch {
    width: 16px;
    height: 8px;
    flex: 0 0 auto;
  }

  .rule {
    width: 16px;
    height: 0;
    border-top: 2px solid var(--color-text);
    flex: 0 0 auto;
  }

  /* 1B's 180 px bar on desktop; the rest of the line on a phone. The marks
     under it are kept, which 1B does not draw: its range alone ("5 – 65 dBZ")
     says where the ramp ends, not what a colour in the middle means. */
  .scale {
    flex: 0 0 180px;
    padding-top: 6px;
  }

  .compact .scale {
    flex: 1 1 80px;
    min-width: 80px;
  }

  /* Two bars share the single bar's room, each named on its own line so the
     marks under a bar line up with that bar. */
  .scale.band {
    flex: 0 0 140px;
    display: flex;
    align-items: flex-start;
    gap: 6px;
  }

  .compact .scale.band {
    flex: 1 1 80px;
    min-width: 80px;
  }

  .band-ramp {
    flex: 1 1 auto;
    min-width: 0;
  }

  .band-name {
    font-size: 10px;
    line-height: 8px;
    color: var(--color-muted);
  }

  .bar {
    height: 8px;
  }

  .marks {
    position: relative;
    height: 13px;
    font-size: 10px;
    color: var(--color-muted);
    font-variant-numeric: tabular-nums;
  }

  .marks span {
    position: absolute;
    transform: translateX(-50%);
    white-space: nowrap;
  }

  /* The first and last labels would otherwise hang off the bar. */
  .marks span:first-child {
    transform: none;
  }

  .marks span:last-child {
    transform: translateX(-100%);
  }

  .units {
    flex: 0 0 auto;
    color: var(--color-muted);
    white-space: nowrap;
  }

  .note {
    margin: 2px 0 0;
    font-size: 11px;
    line-height: 1.35;
    color: var(--color-muted);
  }

  /* Smaller than the note: it is a credit, not a reading. It matches the size
     MapLibre gives its own attribution bar, which is what this stands in for. */
  .credit {
    margin: 2px 0 0;
    font-size: 10px;
    line-height: 1.3;
    color: var(--color-muted);
  }
</style>
